import React, { useEffect, useState, useCallback } from 'react';
import { 
  AnalyticalTable, 
  Button, 
  Dialog, 
  Title, 
  Select, 
  Option, 
  BusyIndicator, 
  Input, 
  Bar,
  Form,
  FormItem,
  FormGroup,
  MessageBox,
  MessageBoxType,
  MessageBoxAction,
  Text,
  Icon,
  Label,
  MultiComboBox,
  MultiComboBoxItem
} from '@ui5/webcomponents-react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../utils/axiosInstance';
import Layout from '../Layout';
import withPermission from '../withPermission';
import usePermission from '../../hooks/usePermission';

const RoleList = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermission();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [sortConfig, setSortConfig] = useState({
    sortBy: 'roleName',
    sortDirection: 'asc'
  });
  const [filteredData, setFilteredData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [formData, setFormData] = useState({
    roleName: '',
    permissions: []
  });
  const [formErrors, setFormErrors] = useState({});

  const fetchPermissions = useCallback(async () => {
    try {
      const response = await axiosInstance.get('permissions');
      if (response.data) {
        setPermissions(response.data);
      }
    } catch (err) {
      console.error('Error al cargar los permisos:', err);
    }
  }, []);

  const fetchRoles = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const url = `roles?page=${page}&size=${pageSize}`;
      const response = await axiosInstance.get(url);
      if (response.data) {
        setData(response.data);
        setTotalPages(Math.ceil(response.data.length / pageSize));
      }
    } catch (err) {
      setError('Error al cargar los roles: ' + (err.message || 'Error desconocido'));
    } finally {
      setLoading(false);
    }
  }, [pageSize]);

  useEffect(() => {
    fetchRoles(currentPage);
    fetchPermissions();
  }, [currentPage, fetchRoles, fetchPermissions]);

  useEffect(() => {
    let filtered = [...data];
    
    if (searchTerm) {
      filtered = filtered.filter(role => 
        role.roleName.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (sortConfig.sortBy) {
      filtered.sort((a, b) => {
        if (a[sortConfig.sortBy] < b[sortConfig.sortBy]) {
          return sortConfig.sortDirection === 'asc' ? -1 : 1;
        }
        if (a[sortConfig.sortBy] > b[sortConfig.sortBy]) {
          return sortConfig.sortDirection === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    
    setFilteredData(filtered);
  }, [data, searchTerm, sortConfig]);

  const handleSort = (event) => {
    const column = event.detail.column;
    const sortDirection = event.detail.sortDirection;
  
    setSortConfig({
      sortBy: column.accessor,
      sortDirection: sortDirection
    });
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleRowClick = (event) => {
    if (!hasPermission('roles', 'update')) {
      return;
    }
    
    const { detail } = event;
    if (detail && detail.row && detail.row.original) {
      const role = detail.row.original;
      navigate(`/roles/edit/${role.id}`);
    }
  };

  const handleCreateRole = () => {
    if (!hasPermission('roles', 'create')) {
      return;
    }
    
    navigate('/roles/create');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const handlePermissionsChange = (event) => {
    const selectedPermissions = event.detail.items.map(item => ({
      id: item.dataset.id,
      name: item.dataset.name
    }));
    
    setFormData(prev => ({
      ...prev,
      permissions: selectedPermissions
    }));
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.roleName.trim()) {
      errors.roleName = 'El nombre del rol es obligatorio';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmitCreate = async () => {
    if (!validateForm()) return;
    
    setLoading(true);
    try {
      const dataToSend = {
        roleName: formData.roleName,
        permissions: formData.permissions.map(p => p.id)
      };
      
      const response = await axiosInstance.post('roles', dataToSend);
      
      if (response.status === 201 || response.status === 200) {
        setShowCreateDialog(false);
        fetchRoles(currentPage);
      } else {
        setFormErrors({
          general: response.data.message || 'Error al crear el rol'
        });
      }
    } catch (err) {
      setFormErrors({
        general: err.response?.data?.message || 'Error al crear el rol'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitEdit = async () => {
    if (!validateForm() || !selectedRole) return;
    
    setLoading(true);
    try {
      const dataToSend = {
        roleName: formData.roleName,
        permissions: formData.permissions.map(p => p.id)
      };
      
      const response = await axiosInstance.put(`roles/${selectedRole.id}`, dataToSend);
      
      if (response.status === 200) {
        setShowEditDialog(false);
        fetchRoles(currentPage);
      } else {
        setFormErrors({
          general: response.data.message || 'Error al actualizar el rol'
        });
      }
    } catch (err) {
      setFormErrors({
        general: err.response?.data?.message || 'Error al actualizar el rol'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = () => {
    if (!hasPermission('roles', 'delete')) {
      return;
    }
    
    if (selectedRole) {
      setShowDeleteConfirm(true);
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedRole) return;
    
    try {
      await axiosInstance.delete(`roles/${selectedRole.id}`);
      fetchRoles(currentPage);
      setShowEditDialog(false);
      setShowDeleteConfirm(false);
    } catch (err) {
      setError('Error al eliminar el rol: ' + (err.message || 'Error desconocido'));
    }
  };

  const columns = [
    {
      Header: 'Nombre del Rol',
      accessor: 'roleName',
      sortable: true
    },
    {
      Header: 'Permisos',
      accessor: 'permissions',
      Cell: ({ value }) => (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
          {value?.map((permission, index) => (
            <span key={index} style={{
              background: '#f0f0f0',
              padding: '2px 6px',
              borderRadius: '4px',
              fontSize: '0.85em'
            }}>
              {permission.permissionName}
            </span>
          ))}
        </div>
      )
    }
  ];

  return (
    <Layout>
      <div style={{ padding: '0 20px' }}>
        <Bar
          design="Header"
          startContent={
            <Title level="H4">Listado de roles</Title>
          }
          endContent={
            hasPermission('roles', 'create') && (
              <Button 
                design="Emphasized" 
                onClick={handleCreateRole}
                icon="add"
              >
                Crear Rol
              </Button>
            )
          }
          style={{ marginBottom: '20px', padding: '0 10px' }}
        />

        <div style={{ marginBottom: '20px' }}>
          <Input
            placeholder="Buscar por nombre del rol"
            value={searchTerm}
            onInput={handleSearchChange}
            style={{ width: '300px' }}
            icon="search"
          />
        </div>

        <div style={{ marginBottom: '20px' }}>
          <AnalyticalTable
            data={filteredData}
            columns={columns}
            selectionMode="None"
            onRowClick={hasPermission('roles', 'update') ? handleRowClick : undefined}
            onSort={handleSort}
            sortable
            visibleRows={10}
            minRows={10}
            alternateRowColor
            style={{
              width: '100%',
              '--sapList_HeaderBackground': '#f5f6f7',
              '--sapList_BorderColor': '#e5e5e5',
            }}
            noDataText="No se encontraron roles"
          />
        </div>

        <Dialog
          open={showCreateDialog || showEditDialog}
          onAfterClose={() => showCreateDialog ? setShowCreateDialog(false) : setShowEditDialog(false)}
          header={
            <Bar
              startContent={
                <Title level="H4">
                  {showCreateDialog ? 'Crear Nuevo Rol' : `Editar Rol: ${selectedRole?.roleName}`}
                </Title>
              }
            />
          }
          footer={
            <Bar
              startContent={
                showEditDialog && hasPermission('roles', 'delete') && (
                  <Button design="Negative" onClick={handleDeleteClick} icon="delete">
                    Eliminar
                  </Button>
                )
              }
              endContent={
                <>
                  <Button 
                    design="Emphasized" 
                    onClick={showCreateDialog ? handleSubmitCreate : handleSubmitEdit}
                  >
                    {showCreateDialog ? 'Crear' : 'Guardar'}
                  </Button>
                  <Button 
                    design="Transparent" 
                    onClick={() => showCreateDialog ? setShowCreateDialog(false) : setShowEditDialog(false)}
                  >
                    Cancelar
                  </Button>
                </>
              }
            />
          }
        >
          <Form 
            style={{ padding: '1rem' }}
            titleText="Información del Rol"
            backgroundDesign="Transparent"
          >
            {formErrors.general && (
              <FormItem>
                <Text style={{ color: 'var(--sapErrorColor)', marginBottom: '1rem' }}>
                  {formErrors.general}
                </Text>
              </FormItem>
            )}
            
            <FormGroup titleText="Detalles del Rol">
              <FormItem labelContent={<Label>Nombre del Rol</Label>}>
                <Input 
                  name="roleName"
                  value={formData.roleName}
                  onChange={handleInputChange}
                  valueState={formErrors.roleName ? "Error" : "None"}
                  valueStateMessage={formErrors.roleName}
                  required
                />
              </FormItem>

              <FormItem labelContent={<Label>Permisos</Label>}>
                <MultiComboBox
                  onSelectionChange={handlePermissionsChange}
                  style={{ width: '100%' }}
                >
                  {permissions.map(permission => (
                    <MultiComboBoxItem
                      key={permission.id}
                      text={permission.name}
                      selected={formData.permissions.some(p => p.id === permission.id)}
                      data-id={permission.id}
                      data-name={permission.name}
                    />
                  ))}
                </MultiComboBox>
              </FormItem>
            </FormGroup>
          </Form>
        </Dialog>

        <MessageBox
          open={showDeleteConfirm}
          onClose={(event) => {
            if (event.detail.action === MessageBoxAction.OK) {
              handleConfirmDelete();
            } else {
              setShowDeleteConfirm(false);
            }
          }}
          title="Confirmar eliminación"
          type={MessageBoxType.WARNING}
          actions={[MessageBoxAction.OK, MessageBoxAction.CANCEL]}
        >
          ¿Está seguro que desea eliminar el rol <strong>{selectedRole?.roleName}</strong>? Esta acción no se puede deshacer.
        </MessageBox>
      </div>
    </Layout>
  );
};

export default withPermission(RoleList, 'roles', 'listView'); 